/**
 * El catálogo de precios (6/10/2026): cómo se enseñan sus importes y qué
 * columnas lleva cada tabla. Los datos vienen del servidor
 * (`GET /api/catalogo-precios`, `backend/services/catalogo_precios.py`):
 * aquí no hay ni un precio ni un nombre de la clínica.
 *
 * Un importe se guarda como número y, si lo lleva, con su matiz
 * (`precio_matiz`: {aprox, desde, mas_iva, por}). Se enseña como se escribió
 * —«~120 € + IVA», «99 €/noche»— y se edita en el mismo texto: el servidor
 * lo vuelve a leer con la misma regla que al importar. (Los importes de los
 * ejemplos son inventados: ni un precio de la clínica en el código.)
 *
 * Desde el 28/10/2026 (administración) un tratamiento y una cirugía llevan
 * además `sin_iva` (el desplegable «Con IVA» / «Sin IVA», como el tarifario
 * de colaboradores), `anestesia` (texto corto) y la `duracion` opcional; y el
 * catálogo se organiza en BLOQUES (nivel 1) → MÉDICOS (nivel 2) → partidas,
 * con la lista anterior (sueltas y por apartados) para repartirla a mano.
 */

/** «1.234», «2.345,60»: punto de miles SIEMPRE (el es-ES del navegador no
 *  agrupa los de cuatro cifras y en la clínica se escribe «1.234»). */
function numeroES(valor) {
  const n = Number(valor);
  const entero = Math.abs(n - Math.round(n)) < 0.005;
  const [ent, dec] = Math.abs(n).toFixed(entero ? 0 : 2).split('.');
  const miles = ent.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${n < 0 ? '-' : ''}${miles}${dec ? `,${dec}` : ''}`;
}

/** El importe para leer: «~120 € + IVA», «desde 456 €», «99 €/noche». */
export function textoImporte(valor, matiz) {
  if (valor === null || valor === undefined || valor === '' || Number.isNaN(Number(valor))) return '—';
  const m = matiz || {};
  let t = `${numeroES(valor)} €`;
  if (m.por) t += `/${m.por}`;
  if (m.mas_iva) t += ' + IVA';
  if (m.aprox) t = `~${t}`;
  if (m.desde) t = `desde ${t}`;
  return t;
}

/** El mismo texto para un campo de un formulario (vacío si no hay importe). */
export function importeParaEditar(valor, matiz) {
  const t = textoImporte(valor, matiz);
  return t === '—' ? '' : t;
}

/** «Sin IVA» / «Con IVA»: la casilla de una partida, para la fila. */
export function textoIva(sinIva) {
  return sinIva ? 'Sin IVA' : 'Con IVA';
}

/** Las secciones de partidas: su fila y cómo se llaman. */
export const SECCIONES_PARTIDAS = {
  tratamientos: { fila: 'tratamiento', titulo: 'TRATAMIENTOS', una: 'tratamiento', precio: 'Precio (PVP)' },
  cirugias: { fila: 'cirugia', titulo: 'CIRUGÍAS', una: 'cirugía', precio: 'Precio' },
};
export const PARTIDAS = ['tratamiento', 'cirugia'];

export const NOMBRE_TIPO = {
  bloque: 'bloque',
  medico: 'médico',
  apartado: 'apartado',
  tratamiento: 'tratamiento',
  cirugia: 'cirugía',
  hospital: 'hospital',
  forfait: 'forfait',
  grupo_servicios: 'grupo de servicios',
  servicio: 'servicio',
};

/** La cirugía es el único tipo femenino («la cirugía», «Dada de baja»). */
export function esFemenino(tipo) {
  return tipo === 'cirugia';
}

/** «el tratamiento», «la cirugía», «el grupo de servicios» (6/10/2026). */
export function conArticulo(tipo) {
  const nombre = NOMBRE_TIPO[tipo] || tipo;
  return `${esFemenino(tipo) ? 'la' : 'el'} ${nombre}`;
}

/** Las columnas que se pegan al importar, en orden (las mismas que
 *  `COLUMNAS_IMPORTAR` del servidor, que es quien las lee). Una tabla de
 *  partidas con las columnas de antes (Nombre | Precio | Duración | Nota)
 *  se sigue entendiendo: `COLUMNAS_IMPORTAR_ANTES`. */
export const COLUMNAS_IMPORTAR = {
  tratamiento: ['Nombre', 'Precio (PVP)', 'IVA', 'Anestesia', 'Duración', 'Nota'],
  cirugia: ['Nombre', 'Precio', 'IVA', 'Anestesia', 'Duración', 'Nota'],
  forfait: ['Forfait', 'Importe sin IVA', 'Importe con IVA', 'Con 1 estancia (sin IVA)', 'Con 1 estancia (con IVA)'],
  servicio: ['Concepto', 'Importe sin IVA', 'Importe con IVA', 'Nota'],
};
export const COLUMNAS_IMPORTAR_ANTES = {
  tratamiento: ['Nombre', 'Precio (PVP)', 'Duración', 'Nota'],
  cirugia: ['Nombre', 'Precio', 'Duración', 'Nota'],
};

/** Los campos de cada tipo en su formulario: [campo, etiqueta, clase].
 *  Clases: texto, largo, importe, iva (el desplegable Con/Sin IVA), si_no
 *  (una casilla) y ficha (el desplegable de fichas de médico). */
const PARTIDA_FORMULARIO = (precio) => [
  ['nombre', 'Nombre', 'texto'],
  ['precio', precio, 'importe'],
  ['sin_iva', 'IVA', 'iva'],
  ['anestesia', 'Anestesia (opcional)', 'texto'],
  ['duracion', 'Duración (min, opcional)', 'texto'],
  ['nota', 'Nota (opcional, aparte del IVA)', 'largo'],
];
export const CAMPOS_FORMULARIO = {
  bloque: [
    ['nombre', 'Nombre del bloque', 'texto'],
    ['medico_id', 'Es la lista de un médico (opcional)', 'ficha'],
    ['lista_unica', 'Lista única: sin subapartados de médico (sus cirugías y tratamientos cuelgan directamente del bloque)', 'si_no'],
    ['nota', 'Nota (opcional)', 'largo'],
  ],
  medico: [
    ['medico_id', 'Ficha del médico (opcional: rellena el nombre)', 'ficha'],
    ['nombre', 'Nombre', 'texto'],
    ['nota', 'Nota (opcional)', 'largo'],
  ],
  apartado: [
    ['nombre', 'Nombre', 'texto'],
    ['nota', 'Nota visible (sale debajo del título)', 'largo'],
    ['reglas', 'Reglas de cálculo (informativas, en un desplegable)', 'largo'],
  ],
  tratamiento: PARTIDA_FORMULARIO('Precio PVP'),
  cirugia: PARTIDA_FORMULARIO('Precio'),
  hospital: [
    ['nombre', 'Hospital', 'texto'],
    ['titulo_tarifa', 'Título de la tarifa (p. ej. «Tarifa de ejemplo»)', 'texto'],
    ['vigencia', 'Vigencia (p. ej. «vigente hasta dd/mm/aaaa»)', 'texto'],
    ['nota_cabecera', 'Nota de la cabecera', 'largo'],
    ['nota_final', 'Nota importante (al final)', 'largo'],
  ],
  forfait: [
    ['nombre', 'Forfait', 'texto'],
    ['sin_iva', 'Importe sin IVA', 'importe'],
    ['con_iva', 'Importe con IVA', 'importe'],
    ['estancia_sin_iva', 'Con 1 estancia (sin IVA)', 'importe'],
    ['estancia_con_iva', 'Con 1 estancia (con IVA)', 'importe'],
  ],
  grupo_servicios: [
    ['nombre', 'Nombre del grupo (p. ej. «Grupo de ejemplo»)', 'texto'],
    ['nota', 'Nota (opcional)', 'largo'],
  ],
  servicio: [
    ['nombre', 'Concepto', 'texto'],
    ['sin_iva', 'Importe sin IVA', 'importe'],
    ['con_iva', 'Importe con IVA (opcional)', 'importe'],
    ['nota', 'Nota (opcional)', 'largo'],
  ],
};

/** Lo que se pinta de un elemento en su formulario, ya como texto. */
export function valoresFormulario(tipo, elemento) {
  const out = {};
  (CAMPOS_FORMULARIO[tipo] || []).forEach(([campo, , clase]) => {
    if (clase === 'importe') out[campo] = importeParaEditar(elemento?.[campo], elemento?.[`${campo}_matiz`]);
    else if (clase === 'iva') out[campo] = elemento?.[campo] ? 'sin' : 'con';
    else if (clase === 'si_no') out[campo] = !!elemento?.[campo];
    else out[campo] = elemento?.[campo] ?? '';
  });
  return out;
}

/** El formulario tal como lo manda la pantalla: el desplegable de IVA como
 *  booleano; el resto, como se escribió (el servidor lo lee). */
export function cuerpoFormulario(tipo, form) {
  const out = {};
  (CAMPOS_FORMULARIO[tipo] || []).forEach(([campo, , clase]) => {
    if (clase === 'iva') out[campo] = form[campo] === 'sin';
    else if (clase === 'si_no') out[campo] = !!form[campo];
    else out[campo] = form[campo] ?? '';
  });
  return out;
}

/** El GET puede no traer algo (o traer otra cosa): siempre la misma forma. */
export function catalogoVacio() {
  const partidas = { filas: [], apartados: [] };
  return { bloques: [], tratamientos: partidas, cirugias: partidas, hospitales: [], gestiona_bloques: false };
}

const lista = (v) => (Array.isArray(v) ? v : []);

function sitio(s) {
  return { ...s, cirugias: lista(s?.cirugias), tratamientos: lista(s?.tratamientos) };
}

export function normalizarCatalogo(datos) {
  const d = datos && typeof datos === 'object' && !Array.isArray(datos) ? datos : {};
  const partidas = (p) => ({
    filas: lista(p?.filas),
    apartados: lista(p?.apartados).map((a) => ({
      ...a,
      filas: lista(a?.filas),
      subapartados: lista(a?.subapartados).map((s) => ({ ...s, filas: lista(s?.filas) })),
    })),
  });
  return {
    bloques: lista(d.bloques).map((b) => ({ ...sitio(b), medicos: lista(b?.medicos).map(sitio) })),
    tratamientos: partidas(d.tratamientos),
    cirugias: partidas(d.cirugias),
    hospitales: lista(d.hospitales).map((h) => ({
      ...h,
      forfaits: lista(h?.forfaits),
      grupos: lista(h?.grupos).map((g) => ({ ...g, servicios: lista(g?.servicios) })),
    })),
    gestiona_bloques: d.gestiona_bloques === true,
  };
}

/** Cuántas filas tiene una sección de partidas (para el resumen del desplegable). */
export function cuentaPartidas(p) {
  let n = (p?.filas || []).length;
  (p?.apartados || []).forEach((a) => {
    n += (a.filas || []).length;
    (a.subapartados || []).forEach((s) => { n += (s.filas || []).length; });
  });
  return n;
}

/** Cuántas partidas hay en un bloque, contando las de sus médicos. */
export function cuentaBloque(b) {
  let n = (b?.cirugias || []).length + (b?.tratamientos || []).length;
  (b?.medicos || []).forEach((m) => { n += (m.cirugias || []).length + (m.tratamientos || []).length; });
  return n;
}

/** Todas las filas de la lista anterior de una sección (sueltas y por apartados). */
export function filasAnteriores(p) {
  const out = [...(p?.filas || [])];
  (p?.apartados || []).forEach((a) => {
    out.push(...(a.filas || []));
    (a.subapartados || []).forEach((s) => out.push(...(s.filas || [])));
  });
  return out;
}

/** Los sitios a los que se mueve, se añade o se copia una partida: cada
 *  bloque y, dentro, cada médico ([{id, etiqueta, tipo}], en el orden de la
 *  pantalla). Un bloque con médicos también sale (una partida puede colgar
 *  directamente de él). */
export function destinosDe(catalogo) {
  const out = [];
  (catalogo?.bloques || []).forEach((b) => {
    out.push({ id: b.id, etiqueta: b.nombre, tipo: 'bloque' });
    (b.medicos || []).forEach((m) => out.push({ id: m.id, etiqueta: `${b.nombre} ▸ ${m.nombre}`, tipo: 'medico' }));
  });
  return out;
}
